import { Fragment, useContext, useEffect, useState } from 'react';
import { Accordion, AccordionSummary } from '@material-ui/core';
import { InfoCard, Progress } from '@backstage/core-components';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { SystemContext } from '../../../contexts/SystemContext';
import { useGroupMembership, UserContext } from '../../../contexts/UserContext';
import { Alert } from '@material-ui/lab';
import Snackbar from '@mui/material/Snackbar';
import { gql, useMutation } from '@apollo/client';
import {
  DevHubAlert,
  DevHubBox,
  DevHubButton,
  DevHubDynamicIcon,
  DevHubGrid,
  DevHubModal,
  DevHubTextField,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

const CUSTOM_ATTRIBUTE_MUTATION = gql`
  mutation UpdateAppCustomAttribute($input: CustomAttributeUpdateInput!) {
    resources {
      customAttributes {
        updateApplicationCustomAttribute(input: $input) {
          customAttributes {
            key
            value
          }
          success
        }
      }
    }
  }
`;

/**
 * Custom Attributes component
 * @constructor
 */
export const CustomAttributes = () => {
  const { app, loading, error, GetApplicationQuery, applicationVariables } =
    useContext(SystemContext);
  const rows = app?.customAttributes || [];
  const [expanded, setExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const { login, hasGroupMembership } = useContext(UserContext);
  const [canEdit, setEdit] = useState<boolean | undefined>(undefined);
  const [successMessage, setSuccessMessage] = useState<string | undefined>(
    undefined,
  );
  const [editedAttribute, setEditedAttribute] = useState<string | undefined>(
    undefined,
  );
  const [editedCustomAttributes, setEditedCustomAttributes] = useState<{
    [key: string]: string;
  }>({});
  const { value: groupData, loading: groupLoading } = useGroupMembership(
    login,
    ['CHQ - DevHub_Custom_Attribute_Mgmt'],
  );

  useEffect(() => {
    if (groupLoading || !groupData) return;
    const canEditInfo = hasGroupMembership(
      groupData,
      'CHQ - DevHub_Custom_Attribute_Mgmt',
    );
    setEdit(canEditInfo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupLoading, groupData]);

  const allowedEditKeys = [
    'Third Party Id',
    'Relationship Type',
    'Engagement Number',
    'Third Party Name',
    'Third Party Relationships',
  ];

  const [editCustomAttribute, { loading: mutationLoading }] = useMutation(
    CUSTOM_ATTRIBUTE_MUTATION,
    {
      refetchQueries: [
        { query: GetApplicationQuery, variables: applicationVariables },
        'GetApplicationDetails',
      ],
    },
  );

  useEffect(() => {
    if (localStorage.getItem('theme') === 'devhub-dark') {
      import('primereact/resources/themes/md-dark-indigo/theme.css');
    } else {
      import('primereact/resources/themes/md-light-indigo/theme.css');
    }
  }, []);

  const handleClose = () => {
    setModalIsOpen(false);
    setEditedAttribute(undefined);
    setEditedCustomAttributes({});
  };

  const handleSave = async () => {
    const changes = Object.entries(editedCustomAttributes).map(
      ([key, value]) => ({
        key,
        value,
      }),
    );

    const appId = app.applicationId;
    const changesWithAppId = {
      applicationId: appId,
      customAttributes: changes,
    };

    await editCustomAttribute({ variables: { input: changesWithAppId } });

    handleClose();
    setIsEditing(false);
    setEditedAttribute(undefined);
    setEditedCustomAttributes({});
    setSuccessMessage('Custom Attribute successfully updated!');
  };

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  const handleEdit = (key: string) => {
    if (allowedEditKeys.includes(key)) {
      setIsEditing(true);
      setModalIsOpen(true);
      setEditedAttribute(key);
    }
  };

  return (
    <>
      <Snackbar
        open={mutationLoading}
        onClose={() => {}}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Alert onClose={() => {}} severity="info">
          Saving changes...
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(undefined)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Alert onClose={() => setSuccessMessage(undefined)} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>
      <InfoCard title="Custom Attributes">
        <Accordion expanded={expanded} onChange={toggleExpanded}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            {loading ? (
              <Progress />
            ) : (
              <Fragment>
                {!expanded ? (
                  <DevHubGrid item xs={12}>
                    <DevHubAlert severity="info" variant="outlined">
                      Found {app?.customAttributes?.length} custom attributes.
                    </DevHubAlert>
                  </DevHubGrid>
                ) : null}
              </Fragment>
            )}
            {expanded && (
              <DevHubGrid container>
                <DevHubGrid item xs={12}>
                  <DevHubAlert severity="info" variant="outlined">
                    Showing {app?.customAttributes?.length} custom attributes.
                  </DevHubAlert>
                </DevHubGrid>
              </DevHubGrid>
            )}
          </AccordionSummary>
          {error && (
            <DevHubAlert severity="error">
              Error fetching custom attributes: {error.message}
            </DevHubAlert>
          )}
          {app?.customAttributes?.length > 0 && (
            <DataTable value={rows} dataKey="key" paginator rows={10}>
              <Column field="key" header="Key" />
              <Column
                field="value"
                header="Value"
                body={rowData =>
                  rowData.key === editedAttribute ? (
                    <DevHubTextField
                      multiline
                      label={rowData.key}
                      value={
                        editedCustomAttributes[rowData.key] || rowData.value
                      }
                      onChange={(event: { target: { value: any } }) => {
                        const newValue = event.target.value;
                        setEditedCustomAttributes({
                          ...editedCustomAttributes,
                          [rowData.key]: newValue,
                        });
                      }}
                    />
                  ) : (
                    rowData.value
                  )
                }
              />
              {!groupLoading && canEdit && (
                <Column
                  header="Actions"
                  body={rowData =>
                    allowedEditKeys.includes(rowData.key) && (
                      <DevHubButton
                        height="large"
                        variant="text"
                        startIcon={<DevHubDynamicIcon iconName="Edit" />}
                        onClick={() => handleEdit(rowData.key)}
                      >
                        Edit
                      </DevHubButton>
                    )
                  }
                />
              )}
            </DataTable>
          )}
        </Accordion>

        {/* Modal for editing custom attributes */}
        <DevHubModal open={modalIsOpen} onClose={handleClose}>
          <Fragment>
            <DevHubBox>
              <DevHubBox display="flex" justifyContent="space-between">
                <DevHubTypography variant="h4" fontColor="red">
                  Edit Custom Attributes
                </DevHubTypography>
                {isEditing && (
                  <DevHubBox>
                    <DevHubGrid container justifyContent="flex-end">
                      <DevHubGrid item>
                        <DevHubButton
                          variant="contained"
                          buttonColor="green"
                          size="small"
                          onClick={handleSave}
                          startIcon={<DevHubDynamicIcon iconName="Save" />}
                        >
                          Save
                        </DevHubButton>
                      </DevHubGrid>
                      <DevHubGrid item>
                        <DevHubButton
                          variant="contained"
                          buttonColor="red"
                          size="small"
                          onClick={handleClose}
                          startIcon={<DevHubDynamicIcon iconName="Clear" />}
                        >
                          Cancel
                        </DevHubButton>
                      </DevHubGrid>
                    </DevHubGrid>
                  </DevHubBox>
                )}
              </DevHubBox>
              <DevHubGrid>
                {isEditing && (
                  <DevHubGrid item xs={11}>
                    <DevHubTextField
                      multiline
                      label={editedAttribute}
                      value={
                        editedCustomAttributes[editedAttribute!] ||
                        rows.find(row => row.key === editedAttribute)?.value
                      }
                      onChange={(event: { target: { value: any } }) => {
                        const newValue = event.target.value;
                        setEditedCustomAttributes(prevState => ({
                          ...prevState,
                          [editedAttribute!]: newValue,
                        }));
                      }}
                    />
                  </DevHubGrid>
                )}
              </DevHubGrid>
            </DevHubBox>
          </Fragment>
        </DevHubModal>
      </InfoCard>
    </>
  );
};
